import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import {
  ActivityIndicator,
  Modal,
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Animated,
  Easing,
} from "react-native";
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ClipboardList,
  Dna,
  Edit3,
  FileText,
  FlaskConical,
  Microscope,
  Plus,
  ScanLine,
  TestTube2,
  X,
  type LucideIcon,
} from "lucide-react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import { EMBER } from "../../onboarding/theme";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  ClinicalDataStatus,
  ListRowSkeleton,
  MoonFilterChip,
  MoonSearchInput,
} from "@louise/moon-ui";
import type { LabResult } from "./care/sections/ResultsLabsCard";
import TestResultDetailDialog from "../components/TestResultDetailDialog";
import {
  useTestResults,
  useDeleteTestResult,
  useUpdateTestResult,
  useCreateTestResult,
  type TestResult,
  type TestResultType,
} from "@src/packages/test-results/hooks/useTestResults";
import { groupCanonicalTestResults } from "@src/packages/test-results";
import {
  CYCLE_TYPE_LABELS,
  useActiveCycleWithPhases,
  useCycles,
  useCycleTypeLabels,
  type Cycle,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import type { ResultInterpretation } from "@src/packages/homepage/plan/intelligence/useTestResultInsights";
import {
  getTestResultInterpretationContext,
  isCycleContextSensitiveTest,
  resolveResultInterpretationContext,
  type ResultInterpretationContext,
  type ResultPhaseLike,
} from "@src/packages/test-results/interpretation-context";
import {
  DocumentTestResultRow,
  type DocumentRowBadge,
} from "./documents/DocumentRow";
import DocumentUploadModal, {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentUploadModal";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import { DEV_PREVIEW_AMH_RESULT } from "../dev/labResultsFixture";
import {
  getCanonicalTestIdForDataPoint,
  getCanonicalTestIdForMarkerName,
  getTestRouteTarget,
} from "./access/testRouteIds";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Props = NativeStackScreenProps<CareStackParamList, "AllLabResults"> & {
  results?: LabResult[];
};

type DisplayLabResult = LabResult & {
  sourceType?: TestResultType | null;
  cycleId?: string | null;
  cycleLabel?: string | null;
};

type LabResultTypeFilterId =
  | "all"
  | "blood"
  | "imaging"
  | "genetics"
  | "urine"
  | "semenFluidAnalysis"
  | "pathology"
  | "other";

type LabResultStatusFilterId = "all" | "resulted" | "pending" | "needs_review";

type LabResultCycleFilterId =
  "all" | "current" | "unlinked" | `cycle:${string}`;

type LabResultFilterOption<TId extends string = string> = {
  id: TId;
  label: string;
  icon?: LucideIcon;
};

type LabResultFilterChipOption = LabResultFilterOption & {
  selected: boolean;
  onPress: () => void;
};

function getDevPreviewLabResultsEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_preview_lab_results") === "true" ||
      params.get("dev_preview_all_cards") === "true"
    );
  } catch {
    return false;
  }
}

function getDevPreviewLabResultsState() {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("dev_lab_results_state");
    return state === "empty" || state === "filled" ? state : null;
  } catch {
    return null;
  }
}

function getDevPreviewLabResultName(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(
      "dev_preview_lab_result",
    );
    return value?.trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

function getDevPreviewLabContext(): "baseline" | "unknown" {
  if (!__DEV__ || typeof window === "undefined") return "baseline";
  try {
    return new URLSearchParams(window.location.search).get(
      "dev_lab_context",
    ) === "unknown"
      ? "unknown"
      : "baseline";
  } catch {
    return "baseline";
  }
}

function getDevPreviewInterpretationContext(
  context: ResultInterpretationContext,
): ResultInterpretationContext {
  return getDevPreviewLabContext() === "unknown"
    ? {
        cycleDay: null,
        cyclePhase: "unknown",
        treatmentPhase: null,
        usedContext: "unknown",
      }
    : context;
}

const DEV_FSH_INTERPRETATION_CONTEXT = getDevPreviewInterpretationContext({
  cycleDay: 3,
  cyclePhase: "baseline",
  treatmentPhase: "pre_treatment",
  usedContext: "baseline",
});

const DEV_MONITORING_INTERPRETATION_CONTEXT =
  getDevPreviewInterpretationContext({
    cycleDay: 8,
    cyclePhase: "follicular",
    treatmentPhase: "protocol",
    usedContext: "stimulation",
  });

const DEV_PREVIEW_LAB_RESULTS: DisplayLabResult[] = [
  DEV_PREVIEW_AMH_RESULT,
  {
    id: "dev-preview-fsh",
    name: "FSH",
    value: "7.1",
    unit: "mIU/mL",
    date: "May 19",
    sourceType: "blood",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "none",
    interpretationContext: DEV_FSH_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("FSH") ?? {}),
  },
  {
    id: "dev-preview-vitamin-d",
    name: "Vitamin D",
    value: "24",
    unit: "ng/mL",
    date: "May 20",
    status: "waiting",
    sourceType: "blood",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "none",
    ...(getBiomarkerFactsheet("Vitamin D") ?? {}),
  },
  {
    id: "dev-preview-endometrial-thickness",
    name: "Endometrial Thickness",
    value: "8.2",
    unit: "mm",
    date: "May 24",
    sourceType: "imaging",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "up",
    interpretationContext: DEV_MONITORING_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("Endometrial Thickness") ?? {}),
  },
  {
    id: "dev-preview-follicle-count",
    name: "Follicle Count (AFC)",
    value: "12",
    unit: "follicles",
    date: "May 24",
    sourceType: "imaging",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "up",
    interpretationContext: DEV_MONITORING_INTERPRETATION_CONTEXT,
    ...(getBiomarkerFactsheet("Follicle Count (AFC)") ?? {}),
  },
  {
    id: "dev-preview-tsh",
    name: "TSH",
    value: "",
    unit: "",
    date: "May 22",
    status: "scheduled",
    sourceType: "blood",
    cycleId: "dev-cycle-past",
    cycleLabel: "IVF Cycle 1",
    trend: "none",
    ...(getBiomarkerFactsheet("TSH") ?? {}),
  },
];

const LAB_EMPTY_CHIPS: { labelKey: string; icon: LucideIcon }[] = [
  { labelKey: "all_lab_results.empty_chip_blood_work", icon: TestTube2 },
  { labelKey: "all_lab_results.empty_chip_hormones", icon: FlaskConical },
  { labelKey: "all_lab_results.empty_chip_ultrasounds", icon: ScanLine },
  { labelKey: "all_lab_results.empty_chip_imaging", icon: Microscope },
  { labelKey: "all_lab_results.empty_chip_genetics", icon: Dna },
  { labelKey: "all_lab_results.empty_chip_semen", icon: ClipboardList },
  { labelKey: "all_lab_results.empty_chip_pathology", icon: Microscope },
  { labelKey: "all_lab_results.empty_chip_full_records", icon: FileText },
];

const MANUAL_RESULT_TYPES: {
  type: TestResultType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    type: "blood",
    labelKey: "all_lab_results.manual_type_blood",
    icon: TestTube2,
  },
  {
    type: "imaging",
    labelKey: "all_lab_results.manual_type_imaging",
    icon: ScanLine,
  },
  {
    type: "genetics",
    labelKey: "all_lab_results.manual_type_genetics",
    icon: Dna,
  },
  {
    type: "semenFluidAnalysis",
    labelKey: "all_lab_results.manual_type_semen",
    icon: ClipboardList,
  },
  {
    type: "urine",
    labelKey: "all_lab_results.manual_type_urine",
    icon: FlaskConical,
  },
  {
    type: "clinical",
    labelKey: "all_lab_results.manual_type_other",
    icon: FileText,
  },
];

const LAB_RESULT_TYPE_FILTERS: {
  id: LabResultTypeFilterId;
  labelKey: string;
  icon?: LucideIcon;
}[] = [
  { id: "blood", labelKey: "all_lab_results.filter_blood", icon: TestTube2 },
  { id: "imaging", labelKey: "all_lab_results.filter_imaging", icon: ScanLine },
  { id: "genetics", labelKey: "all_lab_results.filter_genetics", icon: Dna },
  {
    id: "semenFluidAnalysis",
    labelKey: "all_lab_results.filter_semen",
    icon: ClipboardList,
  },
  { id: "urine", labelKey: "all_lab_results.filter_urine", icon: FlaskConical },
  {
    id: "pathology",
    labelKey: "all_lab_results.filter_pathology",
    icon: Microscope,
  },
  { id: "other", labelKey: "all_lab_results.filter_other", icon: FileText },
];

const LAB_RESULT_STATUS_FILTERS: {
  id: LabResultStatusFilterId;
  labelKey: string;
  icon?: LucideIcon;
}[] = [
  {
    id: "resulted",
    labelKey: "all_lab_results.filter_resulted",
    icon: TestTube2,
  },
  {
    id: "pending",
    labelKey: "all_lab_results.filter_pending",
    icon: ClipboardList,
  },
  {
    id: "needs_review",
    labelKey: "all_lab_results.filter_needs_review",
    icon: AlertCircle,
  },
];

function getResultCategory(
  result: LabResult,
): ResultInterpretation["category"] {
  // Trust the backend's assessment when available — it preserves
  // direction (low vs high), unlike the trend heuristic below.
  if (result.clinicalAssessment === "low") return "low";
  if (result.clinicalAssessment === "high") return "high";
  if (result.clinicalAssessment === "normal") return "normal";
  // Fallback: no assessment from the API. Trend is a weak signal
  // (it just compares to the previous value), so treat any abnormal
  // flag as borderline and trend arrows as direction hints.
  if (result.isAbnormal) return "borderline";
  if (result.trend === "up") return "high";
  if (result.trend === "down") return "low";
  return "normal";
}

function getResultPurpose(result: LabResult, t: TranslationFunction) {
  const normalName = result.name.toLowerCase();
  if (normalName.includes("amh")) {
    return t("all_lab_results.purpose_amh");
  }
  if (normalName.includes("fsh")) {
    return t("all_lab_results.purpose_fsh");
  }
  if (normalName.includes("vitamin d")) {
    return t("all_lab_results.purpose_vitamin_d");
  }
  if (normalName.includes("tsh") || normalName.includes("thyroid")) {
    return t("all_lab_results.purpose_tsh");
  }
  return result.description ?? t("all_lab_results.purpose_default");
}

function labResultToInsight(
  result: LabResult,
  t: TranslationFunction,
): ResultInterpretation {
  const category = getResultCategory(result);
  return {
    testName: result.name,
    testPurpose: getResultPurpose(result, t),
    value: result.value || t("all_lab_results.see_details"),
    unit: result.unit,
    category,
    headline: result.isAbnormal
      ? t("all_lab_results.needs_review", { params: { name: result.name } })
      : result.name,
    interpretation: result.description ?? "",
    whatThisMeans: result.whyItMatters ?? "",
    nextStep: result.normalRange
      ? t("all_lab_results.compare_with_range", {
          params: { range: result.normalRange },
        })
      : undefined,
  };
}

function getResultRowBadge(
  result: LabResult,
  t: TranslationFunction,
): DocumentRowBadge | undefined {
  if (result.status === "waiting" || /waiting/i.test(result.date)) {
    return { label: t("all_lab_results.badge_waiting"), tone: "gold" };
  }
  if (result.status === "scheduled" || /scheduled/i.test(result.date)) {
    return { label: t("all_lab_results.badge_scheduled"), tone: "neutral" };
  }
  return undefined;
}

function isResultPending(result: LabResult) {
  return (
    result.status === "waiting" ||
    result.status === "scheduled" ||
    /waiting|scheduled/i.test(result.date)
  );
}

function inferResultType(result: DisplayLabResult): TestResultType | null {
  if (result.sourceType) return result.sourceType;

  const name = result.name.toLowerCase();
  if (
    /\b(amh|fsh|lh|tsh|hcg|estradiol|progesterone|vitamin d|prolactin|cbc|glucose|thyroid)\b/i.test(
      name,
    )
  ) {
    return "blood";
  }
  if (
    /\b(ultrasound|sono|scan|mri|x-ray|xray|ct|afc|follicle|endometrium|hsg)\b/i.test(
      name,
    )
  ) {
    return "imaging";
  }
  if (/\b(genetic|carrier|karyotype|dna|pgta|pgt-a|pgs)\b/i.test(name)) {
    return "genetics";
  }
  if (/\b(semen|sperm|motility|morphology)\b/i.test(name)) {
    return "semenFluidAnalysis";
  }
  if (/\b(urine|urinalysis)\b/i.test(name)) {
    return "urine";
  }
  return null;
}

function labResultMatchesTypeFilter(
  result: DisplayLabResult,
  filterId: LabResultTypeFilterId,
) {
  if (filterId === "all") return true;
  const type = inferResultType(result);
  if (filterId === "pathology") return type === "biopsy" || type === "sampling";
  if (filterId === "other") return type === "clinical" || type === null;
  return type === filterId;
}

function labResultMatchesStatusFilter(
  result: DisplayLabResult,
  filterId: LabResultStatusFilterId,
) {
  if (filterId === "all") return true;
  if (filterId === "resulted") return !isResultPending(result);
  if (filterId === "pending") return isResultPending(result);
  return !!result.isAbnormal;
}

function labResultMatchesSearch(result: DisplayLabResult, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return [
    result.name,
    result.value,
    result.unit,
    result.date,
    result.cycleLabel,
  ]
    .filter(Boolean)
    .some((field) => field!.toLowerCase().includes(normalized));
}

function getCycleFilterCycleId(filterId: LabResultCycleFilterId) {
  return filterId.startsWith("cycle:") ? filterId.slice("cycle:".length) : null;
}

function testResultMatchesCycleFilter(
  result: TestResult,
  filterId: LabResultCycleFilterId,
  currentCycleId: string | null,
) {
  if (filterId === "all") return true;
  if (filterId === "current") {
    return !!currentCycleId && result.cycleId === currentCycleId;
  }
  if (filterId === "unlinked") return !result.cycleId;
  return result.cycleId === getCycleFilterCycleId(filterId);
}

function displayResultMatchesCycleFilter(
  result: DisplayLabResult,
  filterId: LabResultCycleFilterId,
  currentCycleId: string | null,
) {
  if (filterId === "all") return true;
  if (filterId === "current") {
    return !!currentCycleId && result.cycleId === currentCycleId;
  }
  if (filterId === "unlinked") return !result.cycleId;
  return result.cycleId === getCycleFilterCycleId(filterId);
}

function getRawResultInterpretationContext(
  result: TestResult,
  cycles: Cycle[],
  activeCycle: {
    cycle?: { id?: string | null; startDate?: string | null } | null;
    phases?: ResultPhaseLike[];
  },
): ResultInterpretationContext | undefined {
  if (!result.name || !isCycleContextSensitiveTest(result.name)) {
    return undefined;
  }

  const activeCycleId = activeCycle.cycle?.id ?? null;
  const isActiveCycleResult =
    !result.cycleId || !activeCycleId || result.cycleId === activeCycleId;
  const linkedCycle = result.cycleId
    ? cycles.find((cycle) => cycle.id === result.cycleId)
    : null;
  const base = resolveResultInterpretationContext({
    resultDate: result.date,
    cycleStartDate:
      linkedCycle?.startDate ??
      (isActiveCycleResult ? (activeCycle.cycle?.startDate ?? null) : null),
    phaseId: isActiveCycleResult ? result.phaseId : null,
    phases: isActiveCycleResult ? (activeCycle.phases ?? []) : [],
  });
  return getTestResultInterpretationContext(result.name, base);
}

function buildDisplayLabResults(
  sourceResults: TestResult[],
  abbrev: (date: string) => string,
  t: TranslationFunction,
  locale: Locale,
  cycleLabelById: Record<string, string>,
  contextByResultId: Record<string, ResultInterpretationContext> = {},
): DisplayLabResult[] {
  const unknownLabel = t("all_lab_results.name_unknown");
  return groupCanonicalTestResults(sourceResults).map((historyGroup) => {
    const latest = historyGroup.latest;
    const history = historyGroup.results.map((result) => ({
      id: result.id,
      date: result.date ? abbrev(result.date) : "",
      value: result.value,
    }));
    const factsheet = latest.name
      ? getBiomarkerFactsheet(latest.name, locale)
      : null;
    return {
      id: latest.id,
      dataPointId: latest.dataPointId,
      testId:
        getCanonicalTestIdForDataPoint(latest.dataPointId) ??
        getCanonicalTestIdForMarkerName(latest.name),
      name: latest.name ?? unknownLabel,
      value: latest.value,
      unit: latest.unit ?? "",
      date: latest.date ? abbrev(latest.date) : "",
      ...(factsheet ?? {}),
      sourceType: latest.type,
      cycleId: latest.cycleId ?? null,
      cycleLabel: latest.cycleId
        ? (cycleLabelById[latest.cycleId] ?? null)
        : null,
      cycleLink: latest.cycleId
        ? {
            label:
              cycleLabelById[latest.cycleId] ??
              t("all_lab_results.filter_cycle_fallback"),
            cycleId: latest.cycleId,
          }
        : undefined,
      trend: historyGroup.trend,
      clinicalAssessment: latest.clinicalAssessment,
      isAbnormal:
        latest.clinicalAssessment === "low" ||
        latest.clinicalAssessment === "high",
      history: history.length > 1 ? history : undefined,
      interpretationContext: contextByResultId[latest.id],
      provenance: latest.source,
      attachment: latest.source.attachment,
    };
  });
}

function localizeLabResultFactsheet(
  result: DisplayLabResult,
  locale: Locale,
): DisplayLabResult {
  return {
    ...result,
    testId:
      result.testId ??
      getCanonicalTestIdForDataPoint(result.dataPointId) ??
      getCanonicalTestIdForMarkerName(result.name),
    ...(getBiomarkerFactsheet(result.name, locale) ?? {}),
    cycleLink:
      result.cycleLink ??
      (result.cycleId && result.cycleLabel
        ? { label: result.cycleLabel, cycleId: result.cycleId }
        : undefined),
  };
}

function getCycleSortValue(cycle: Cycle) {
  return new Date(cycle.startDate ?? cycle.createdAt ?? "").getTime();
}

function buildCycleLabels(
  cycles: Cycle[],
  cycleTypeLabels: Partial<Record<CycleType, string>>,
) {
  const sorted = [...cycles].sort(
    (a, b) => getCycleSortValue(a) - getCycleSortValue(b),
  );
  const countByType: Partial<Record<CycleType, number>> = {};
  const indexById: Record<string, number> = {};

  for (const cycle of sorted) {
    countByType[cycle.cycleType] = (countByType[cycle.cycleType] ?? 0) + 1;
    indexById[cycle.id] = countByType[cycle.cycleType]!;
  }

  return Object.fromEntries(
    cycles.map((cycle) => {
      const typeLabel =
        cycleTypeLabels[cycle.cycleType] ??
        CYCLE_TYPE_LABELS[cycle.cycleType] ??
        "Cycle";
      const shouldNumber = (countByType[cycle.cycleType] ?? 0) > 1;
      return [
        cycle.id,
        shouldNumber ? `${typeLabel} ${indexById[cycle.id] ?? 1}` : typeLabel,
      ];
    }),
  );
}

function buildCycleFilterOptions(
  results: DisplayLabResult[],
  cycles: Cycle[],
  currentCycleId: string | null,
  cycleLabelById: Record<string, string>,
  t: TranslationFunction,
): LabResultFilterOption<LabResultCycleFilterId>[] {
  const cycleIds = Array.from(
    new Set(
      results
        .map((result) => result.cycleId)
        .filter((cycleId): cycleId is string => !!cycleId),
    ),
  );
  const hasUnlinked = results.some((result) => !result.cycleId);
  if (cycleIds.length + (hasUnlinked ? 1 : 0) <= 1) return [];

  const cycleSortOrder = new Map(
    [...cycles]
      .sort((a, b) => getCycleSortValue(a) - getCycleSortValue(b))
      .map((cycle, index) => [cycle.id, index]),
  );
  const options: LabResultFilterOption<LabResultCycleFilterId>[] = [
    {
      id: "all",
      label: t("all_lab_results.filter_all_cycles"),
      icon: Calendar,
    },
  ];

  if (currentCycleId && cycleIds.includes(currentCycleId)) {
    options.push({
      id: "current",
      label: t("all_lab_results.filter_current_cycle"),
      icon: Calendar,
    });
  }

  cycleIds
    .filter((cycleId) => cycleId !== currentCycleId)
    .sort((a, b) => {
      const aOrder = cycleSortOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = cycleSortOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    })
    .forEach((cycleId) => {
      const displayLabel =
        results.find((result) => result.cycleId === cycleId)?.cycleLabel ??
        cycleLabelById[cycleId] ??
        t("all_lab_results.filter_cycle_fallback");
      options.push({
        id: `cycle:${cycleId}`,
        label: displayLabel,
        icon: Calendar,
      });
    });

  if (hasUnlinked) {
    options.push({
      id: "unlinked",
      label: t("all_lab_results.filter_no_cycle"),
      icon: Calendar,
    });
  }

  return options;
}

function StaticLabResultRailChip({
  label,
  icon: Icon,
}: {
  label: string;
  icon: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 20,
        borderWidth: 1,
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        borderColor: "rgba(0, 0, 0, 0.06)",
      }}
    >
      <Icon size={14} color={EMBER.colors.textMid} style={{ marginRight: 6 }} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "400",
          color: EMBER.colors.textMid,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function MovingLabResultChipRail({
  pills,
}: {
  pills: { label: string; icon: LucideIcon }[];
}) {
  const offset = useRef(new Animated.Value(0)).current;
  const [sequenceWidth, setSequenceWidth] = useState(0);

  useEffect(() => {
    if (sequenceWidth === 0) return;

    offset.setValue(0);
    const loop = Animated.loop(
      Animated.timing(offset, {
        toValue: -sequenceWidth,
        duration: Math.max(12000, sequenceWidth * 32),
        easing: Easing.linear,
        useNativeDriver: true,
      }),
      { resetBeforeIteration: true },
    );

    loop.start();
    return () => loop.stop();
  }, [offset, sequenceWidth]);

  const renderPillSet = (copyIndex: number, measure = false) => (
    <View
      key={copyIndex}
      onLayout={
        measure
          ? (event) => setSequenceWidth(event.nativeEvent.layout.width)
          : undefined
      }
      style={{
        flexDirection: "row",
        gap: 8,
        paddingRight: 8,
      }}
    >
      {pills.map((pill) => (
        <StaticLabResultRailChip
          key={`${copyIndex}-${pill.label}`}
          label={pill.label}
          icon={pill.icon}
        />
      ))}
    </View>
  );

  return (
    <View style={{ width: "100%", overflow: "hidden", paddingHorizontal: 2 }}>
      <Animated.View
        style={{
          flexDirection: "row",
          transform: [{ translateX: offset }],
        }}
      >
        {renderPillSet(0, true)}
        {renderPillSet(1)}
      </Animated.View>
    </View>
  );
}

function LabResultFilterRail({
  options,
}: {
  options: LabResultFilterChipOption[];
}) {
  if (options.length === 0) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ marginHorizontal: -16, marginBottom: 16 }}
      contentContainerStyle={{
        gap: 8,
        paddingLeft: 16,
        paddingRight: 24,
        paddingBottom: 2,
      }}
    >
      {options.map((option) => (
        <MoonFilterChip
          key={option.id}
          label={option.label}
          icon={option.icon}
          selected={option.selected}
          onPress={option.onPress}
          tone="glass"
          showCheck={false}
        />
      ))}
    </ScrollView>
  );
}

function LabResultsEmptyState({
  onUpload,
  onFilesSelected,
  onManualEntry,
}: {
  onUpload: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onManualEntry: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("LabResultsEmptyState");
  const pills = LAB_EMPTY_CHIPS.map((chip) => ({
    label: t(chip.labelKey),
    icon: chip.icon,
  }));

  return (
    <View
      style={{
        paddingVertical: 8,
        alignItems: "center",
        overflow: "visible",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          lineHeight: 27,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {t("all_lab_results.empty_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          textAlign: "center",
          lineHeight: 21,
          marginTop: 8,
          maxWidth: 410,
        }}
      >
        {t("all_lab_results.empty_body")}
      </Text>
      <View
        style={{
          width: "100%",
          marginTop: 22,
          marginBottom: 18,
        }}
      >
        <MovingLabResultChipRail pills={pills} />
      </View>
      <View style={{ width: "100%" }}>
        <DocumentFileDropZone
          onPick={onUpload}
          onFilesSelected={onFilesSelected}
        />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: EMBER.colors.textFaint,
          textAlign: "center",
          lineHeight: 17,
          marginTop: 12,
          maxWidth: 420,
        }}
      >
        {t("all_lab_results.empty_note")}
      </Text>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("all_lab_results.empty_manual_entry")),
            "all_lab_results.empty_manual_entry",
          );
          onManualEntry();
        }}
        hitSlop={8}
        style={({ pressed }) => ({
          flexDirection: "row",
          alignItems: "center",
          gap: 5,
          marginTop: 10,
          opacity: pressed ? 0.62 : 1,
        })}
      >
        <Edit3 size={13} color={EMBER.colors.textLight} strokeWidth={1.7} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "600",
            color: EMBER.colors.textLight,
          }}
        >
          {t("all_lab_results.empty_manual_entry")}
        </Text>
      </Pressable>
    </View>
  );
}

function getResultDateLabel(result: LabResult, t: TranslationFunction) {
  const date = result.date.trim();
  if (!date || /waiting|scheduled/i.test(date)) {
    return t("all_lab_results.no_date");
  }
  return date;
}

function getManualResultCategoryFields(type: TestResultType) {
  switch (type) {
    case "blood":
      return { bloodTestCategory: "other" as const };
    case "imaging":
      return { imagingTestCategory: "other" as const };
    case "genetics":
      return { geneticsTestCategory: "other" as const };
    case "semenFluidAnalysis":
      return { semenFluidTestCategory: "basic" as const };
    default:
      return {};
  }
}

function isNumericResultValue(value: string) {
  const normalized = value.trim().replace(",", ".");
  return normalized.length > 0 && Number.isFinite(Number(normalized));
}

function ManualLabResultModal({
  visible,
  onClose,
  onOpenUpload,
  initialName,
  initialType,
}: {
  visible: boolean;
  onClose: () => void;
  onOpenUpload: (files?: SelectedFile[]) => void;
  initialName?: string | null;
  initialType?: TestResultType | null;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("ManualLabResultModal");
  const createTestResult = useCreateTestResult();
  const [type, setType] = useState<TestResultType>(initialType ?? "blood");
  const [name, setName] = useState(initialName ?? "");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setType(initialType ?? "blood");
    setName(initialName ?? "");
    setValue("");
    setUnit("");
    setDate(new Date().toISOString().slice(0, 10));
    setErrorMessage(null);
  }, [initialName, initialType]);

  useEffect(() => {
    if (!visible) return;
    reset();
  }, [reset, visible]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedValue = value.trim();
    const trimmedDate = date.trim();
    if (!trimmedName || !trimmedValue || !trimmedDate) {
      setErrorMessage(t("all_lab_results.manual_error_required"));
      return;
    }

    setErrorMessage(null);
    try {
      await createTestResult.mutateAsync({
        name: trimmedName,
        type,
        patientValue: trimmedValue,
        valueType: isNumericResultValue(trimmedValue) ? "numeric" : "string",
        unit: unit.trim() || null,
        date: trimmedDate,
        notes: t("all_lab_results.manual_notes"),
        ...getManualResultCategoryFields(type),
      });
      handleClose();
    } catch (error) {
      console.warn(
        "[AllLabResultsScreen] Failed to create test result:",
        error,
      );
      setErrorMessage(t("all_lab_results.manual_error_save"));
    }
  }, [createTestResult, date, handleClose, name, t, type, unit, value]);

  if (!visible) return null;

  const hasPresetName = Boolean(initialName?.trim());

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable
        onPress={handleClose}
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          pointerEvents="box-none"
          style={{
            width: "100%",
            alignItems: "center",
          }}
        >
          <Pressable
            onPress={(event) => event.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: 420,
              borderRadius: 20,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                paddingHorizontal: 24,
                paddingTop: 28,
                paddingBottom: 24,
                backgroundColor: "rgba(255,255,255,0.9)",
                ...(Platform.OS === "web"
                  ? {
                      backdropFilter: "blur(24px) saturate(1.4)",
                      WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                    }
                  : null),
              }}
            >
              <Pressable
                onPress={() => {
                  // X-icon with no visible copy to name it.
                  capturePress("close_manual_lab_result");
                  handleClose();
                }}
                hitSlop={8}
                style={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  backgroundColor: "rgba(0,0,0,0.05)",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={14} color={EMBER.colors.textLight} />
              </Pressable>

              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 20,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  paddingRight: 34,
                }}
              >
                {t("all_lab_results.manual_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 18,
                  color: EMBER.colors.textLight,
                  marginTop: 4,
                  marginBottom: 18,
                  paddingRight: 24,
                }}
              >
                {t("all_lab_results.manual_subtitle")}
              </Text>

              <DocumentFileDropZone
                compact
                onPick={() => onOpenUpload()}
                onFilesSelected={onOpenUpload}
              />

              {!hasPresetName ? (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 16,
                    marginBottom: 16,
                  }}
                >
                  {MANUAL_RESULT_TYPES.map((option) => {
                    const Icon = option.icon;
                    const selected = type === option.type;
                    return (
                      <Pressable
                        key={option.type}
                        onPress={() => {
                          capturePress(
                            pressLabel(t(option.labelKey)),
                            "manual_lab_result_type",
                          );
                          setType(option.type);
                        }}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                          paddingHorizontal: 11,
                          paddingVertical: 8,
                          borderRadius: 18,
                          borderWidth: 1,
                          borderColor: selected
                            ? "rgba(122, 62, 21, 0.24)"
                            : "rgba(0, 0, 0, 0.06)",
                          backgroundColor: selected
                            ? "rgba(122, 62, 21, 0.08)"
                            : "rgba(0, 0, 0, 0.035)",
                        }}
                      >
                        <Icon
                          size={13}
                          color={
                            selected ? EMBER.colors.warm : EMBER.colors.textMid
                          }
                          strokeWidth={1.6}
                        />
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            fontWeight: "600",
                            color: selected
                              ? EMBER.colors.warm
                              : EMBER.colors.textMid,
                          }}
                        >
                          {t(option.labelKey)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}

              <View style={{ gap: 10, marginTop: hasPresetName ? 16 : 0 }}>
                <TextInput
                  value={name}
                  onChangeText={setName}
                  placeholder={t("all_lab_results.manual_name_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 11,
                    borderWidth: 1,
                    borderColor: "rgba(0,0,0,0.06)",
                    ...(Platform.OS === "web" && {
                      outlineStyle: "none" as const,
                    }),
                  }}
                />
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <TextInput
                    value={value}
                    onChangeText={setValue}
                    placeholder={t("all_lab_results.manual_value_placeholder")}
                    placeholderTextColor={EMBER.colors.textFaint}
                    style={{
                      flex: 1,
                      fontSize: 14,
                      color: EMBER.colors.text,
                      fontFamily: "DMSans_400Regular",
                      backgroundColor: "rgba(0,0,0,0.03)",
                      borderRadius: 10,
                      paddingHorizontal: 12,
                      paddingVertical: 11,
                      borderWidth: 1,
                      borderColor: "rgba(0,0,0,0.06)",
                      ...(Platform.OS === "web" && {
                        outlineStyle: "none" as const,
                      }),
                    }}
                  />
                  <TextInput
                    value={unit}
                    onChangeText={setUnit}
                    placeholder={t("all_lab_results.manual_unit_placeholder")}
                    placeholderTextColor={EMBER.colors.textFaint}
                    style={{
                      width: 104,
                      fontSize: 14,
                      color: EMBER.colors.text,
                      fontFamily: "DMSans_400Regular",
                      backgroundColor: "rgba(0,0,0,0.03)",
                      borderRadius: 10,
                      paddingHorizontal: 12,
                      paddingVertical: 11,
                      borderWidth: 1,
                      borderColor: "rgba(0,0,0,0.06)",
                      ...(Platform.OS === "web" && {
                        outlineStyle: "none" as const,
                      }),
                    }}
                  />
                </View>
                <TextInput
                  value={date}
                  onChangeText={setDate}
                  placeholder={t("all_lab_results.manual_date_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 11,
                    borderWidth: 1,
                    borderColor: "rgba(0,0,0,0.06)",
                    ...(Platform.OS === "web" && {
                      outlineStyle: "none" as const,
                    }),
                  }}
                />
              </View>

              {errorMessage ? (
                <View
                  style={{
                    flexDirection: "row",
                    gap: 8,
                    alignItems: "center",
                    marginTop: 12,
                    padding: 10,
                    borderRadius: 10,
                    backgroundColor: `${EMBER.colors.coral}10`,
                  }}
                >
                  <AlertCircle
                    size={15}
                    color={EMBER.colors.coral}
                    strokeWidth={1.8}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontSize: 12,
                      color: EMBER.colors.coral,
                      lineHeight: 16,
                    }}
                  >
                    {errorMessage}
                  </Text>
                </View>
              ) : null}

              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("all_lab_results.manual_save")),
                    "all_lab_results.manual_save",
                  );
                  handleSave();
                }}
                disabled={createTestResult.isPending}
                style={{
                  backgroundColor: EMBER.colors.warm,
                  borderRadius: 12,
                  paddingVertical: 13,
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 16,
                  opacity: createTestResult.isPending ? 0.7 : 1,
                }}
              >
                {createTestResult.isPending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text
                    className="font-dm-sans"
                    style={{ fontSize: 14, fontWeight: "700", color: "#fff" }}
                  >
                    {t("all_lab_results.manual_save")}
                  </Text>
                )}
              </Pressable>
            </View>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}

export default function AllLabResultsScreen({
  navigation,
  route,
  results: propResults,
}: Props) {
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const t = useTranslations();
  const capturePress = usePressCapture("AllLabResultsScreen");
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const testResultsQuery = useTestResults();
  const { data: testResults, error: testResultsError } = testResultsQuery;
  const { data: cycles = [] } = useCycles();
  const activeCycle = useActiveCycleWithPhases();
  const cycleTypeLabels = useCycleTypeLabels();
  const { abbrev } = useDateFormat();
  const deleteTestResult = useDeleteTestResult();
  const updateTestResult = useUpdateTestResult();
  const devPreviewLabResults = getDevPreviewLabResultsEnabled();
  const devPreviewLabResultsState = getDevPreviewLabResultsState();
  const devPreviewLabResultName = getDevPreviewLabResultName();
  const devClinicalDataState = getDevClinicalDataState("labs");
  const openTestFactsheet = (testId: string) => {
    setSelectedResult(null);
    const target = getTestRouteTarget(testId);
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: target.screen,
          params: { ...target.params, returnTab: "Care" },
        },
      }),
    );
  };
  const labsDataState = devClinicalDataState
    ? devClinicalDataState
    : devPreviewLabResults || propResults !== undefined
      ? null
      : testResultsError
        ? testResults === undefined
          ? "unavailable"
          : "stale"
        : null;
  const displayLabResultsLoading =
    !devPreviewLabResults &&
    propResults === undefined &&
    testResultsQuery.isLoading;
  const retryLabResults = () => {
    void testResultsQuery.refetch();
  };
  const cycleIdFilter = route.params?.cycleId ?? null;
  const cycleLabel = route.params?.cycleLabel ?? null;
  const initialResultId = route.params?.initialResultId ?? null;
  const [search, setSearch] = useState("");
  const [activeTypeFilter, setActiveTypeFilter] =
    useState<LabResultTypeFilterId>("all");
  const [activeStatusFilter, setActiveStatusFilter] =
    useState<LabResultStatusFilterId>("all");
  const [activeCycleFilter, setActiveCycleFilter] =
    useState<LabResultCycleFilterId>("all");
  const [selectedResult, setSelectedResult] = useState<DisplayLabResult | null>(
    null,
  );
  const devPreviewResultOpened = useRef(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<SelectedFile[]>(
    [],
  );
  const [manualEntryVisible, setManualEntryVisible] = useState(false);
  const [manualEntryName, setManualEntryName] = useState<string | null>(null);
  const [manualEntryType, setManualEntryType] = useState<TestResultType | null>(
    null,
  );
  const uploadButtonDrift = useRef(new Animated.Value(0)).current;

  const openManualEntry = useCallback(
    (name?: string, type?: TestResultType | null) => {
      setManualEntryName(name ?? null);
      setManualEntryType(type ?? null);
      setManualEntryVisible(true);
    },
    [],
  );

  const currentCycleId = useMemo(
    () => cycles.find((cycle) => cycle.status === "open")?.id ?? null,
    [cycles],
  );
  const cycleLabelById = useMemo(
    () => buildCycleLabels(cycles, cycleTypeLabels),
    [cycles, cycleTypeLabels],
  );
  const sourceTestResults = useMemo(() => {
    if (!testResults) return [];
    return cycleIdFilter
      ? testResults.filter((result) => result.cycleId === cycleIdFilter)
      : testResults;
  }, [cycleIdFilter, testResults]);
  const shouldUseDevResults =
    devPreviewLabResultsState === "filled" ||
    (devPreviewLabResults && (!testResults || testResults.length === 0));
  const contextByResultId = useMemo(() => {
    if (!testResults) return {};
    return Object.fromEntries(
      testResults.flatMap((result) => {
        const context = getRawResultInterpretationContext(
          result,
          cycles,
          activeCycle,
        );
        return context ? [[result.id, context]] : [];
      }),
    ) as Record<string, ResultInterpretationContext>;
  }, [activeCycle, cycles, testResults]);

  // Derive lab results from API if no prop results provided.
  const baseResults: DisplayLabResult[] = useMemo(() => {
    if (devPreviewLabResultsState === "empty") return [];
    if (propResults && propResults.length > 0) {
      return propResults.map((result) =>
        localizeLabResultFactsheet(result, locale),
      );
    }
    if (shouldUseDevResults) {
      return DEV_PREVIEW_LAB_RESULTS.map((result) =>
        localizeLabResultFactsheet(result, locale),
      );
    }
    if (!testResults) return [];
    return buildDisplayLabResults(
      sourceTestResults,
      abbrev,
      t,
      locale,
      cycleLabelById,
      contextByResultId,
    );
  }, [
    propResults,
    devPreviewLabResultsState,
    shouldUseDevResults,
    testResults,
    sourceTestResults,
    abbrev,
    cycleLabelById,
    contextByResultId,
    locale,
    t,
  ]);

  const cycleFilterOptions = useMemo(
    () =>
      cycleIdFilter
        ? []
        : buildCycleFilterOptions(
            baseResults,
            cycles,
            currentCycleId,
            cycleLabelById,
            t,
          ),
    [baseResults, cycleIdFilter, cycles, currentCycleId, cycleLabelById, t],
  );

  useEffect(() => {
    if (cycleIdFilter && activeCycleFilter !== "all") {
      setActiveCycleFilter("all");
      return;
    }
    if (
      activeCycleFilter !== "all" &&
      !cycleFilterOptions.some((option) => option.id === activeCycleFilter)
    ) {
      setActiveCycleFilter("all");
    }
  }, [activeCycleFilter, cycleFilterOptions, cycleIdFilter]);

  const results: DisplayLabResult[] = useMemo(() => {
    if (activeCycleFilter === "all" || cycleIdFilter) return baseResults;
    if (
      !devPreviewLabResultsState &&
      (!propResults || propResults.length === 0) &&
      testResults
    ) {
      const cycleScopedResults = sourceTestResults.filter((result) =>
        testResultMatchesCycleFilter(result, activeCycleFilter, currentCycleId),
      );
      return buildDisplayLabResults(
        cycleScopedResults,
        abbrev,
        t,
        locale,
        cycleLabelById,
        contextByResultId,
      );
    }
    return baseResults.filter((result) =>
      displayResultMatchesCycleFilter(
        result,
        activeCycleFilter,
        currentCycleId,
      ),
    );
  }, [
    abbrev,
    activeCycleFilter,
    baseResults,
    currentCycleId,
    cycleIdFilter,
    cycleLabelById,
    contextByResultId,
    devPreviewLabResultsState,
    locale,
    propResults,
    sourceTestResults,
    t,
    testResults,
  ]);

  useEffect(() => {
    if (!initialResultId || selectedResult) return;
    const result = results.find(
      (candidate) => candidate.id === initialResultId,
    );
    if (result) setSelectedResult(result);
  }, [initialResultId, results, selectedResult]);

  useEffect(() => {
    if (!devPreviewLabResultName || devPreviewResultOpened.current) return;
    const result = results.find(
      (candidate) =>
        candidate.id.toLowerCase() === devPreviewLabResultName ||
        candidate.name.toLowerCase() === devPreviewLabResultName,
    );
    if (!result) return;
    devPreviewResultOpened.current = true;
    setSelectedResult(result);
  }, [devPreviewLabResultName, results]);

  const typeFilterOptions = useMemo(
    () =>
      LAB_RESULT_TYPE_FILTERS.map((filter) => ({
        id: filter.id,
        label: t(filter.labelKey),
        icon: filter.icon,
      })),
    [t],
  );
  const statusFilterOptions = useMemo(
    () =>
      LAB_RESULT_STATUS_FILTERS.map((filter) => ({
        id: filter.id,
        label: t(filter.labelKey),
        icon: filter.icon,
      })),
    [t],
  );

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(uploadButtonDrift, {
          toValue: 1,
          duration: 2600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(uploadButtonDrift, {
          toValue: 0,
          duration: 2600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [uploadButtonDrift]);

  const handleUpload = useCallback(() => {
    setPendingUploadFiles([]);
    setUploadModalVisible(true);
  }, []);

  const handleDroppedUpload = useCallback((files: SelectedFile[]) => {
    setPendingUploadFiles(files);
    setUploadModalVisible(true);
  }, []);

  const handleManualUpload = useCallback(
    (files?: SelectedFile[]) => {
      setManualEntryVisible(false);
      if (files && files.length > 0) {
        handleDroppedUpload(files);
        return;
      }
      handleUpload();
    },
    [handleDroppedUpload, handleUpload],
  );

  const handleUploadClose = useCallback(() => {
    setUploadModalVisible(false);
    setPendingUploadFiles([]);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setActiveCycleFilter("all");
    setActiveTypeFilter("all");
    setActiveStatusFilter("all");
  }, []);

  const filtered = results.filter(
    (result) =>
      labResultMatchesSearch(result, search) &&
      labResultMatchesTypeFilter(result, activeTypeFilter) &&
      labResultMatchesStatusFilter(result, activeStatusFilter),
  );
  const filtersActive =
    search.trim().length > 0 ||
    activeTypeFilter !== "all" ||
    activeStatusFilter !== "all" ||
    (!cycleIdFilter && activeCycleFilter !== "all");
  const countLabel =
    filtersActive && filtered.length !== baseResults.length
      ? t(
          filtered.length === 1
            ? "all_lab_results.count_filtered_one"
            : "all_lab_results.count_filtered_other",
          {
            params: {
              count: String(filtered.length),
              total: String(baseResults.length),
            },
          },
        )
      : t(
          filtered.length === 1
            ? "all_lab_results.count_one"
            : "all_lab_results.count_other",
          { params: { count: String(filtered.length) } },
        );
  const noResultsKey = filtersActive
    ? "all_lab_results.no_results_filtered"
    : "all_lab_results.no_results_found";
  const showFilters = baseResults.length > 0;
  const showStatusFilters =
    baseResults.some(isResultPending) ||
    baseResults.some((result) => result.isAbnormal);

  const onTypeFilterChange = useCallback((id: LabResultTypeFilterId) => {
    setActiveTypeFilter((current) => (current === id ? "all" : id));
  }, []);
  const onStatusFilterChange = useCallback((id: LabResultStatusFilterId) => {
    setActiveStatusFilter((current) => (current === id ? "all" : id));
  }, []);
  const onCycleFilterChange = useCallback((id: LabResultCycleFilterId) => {
    setActiveCycleFilter((current) => (current === id ? "all" : id));
  }, []);

  const visibleFilterOptions = useMemo<LabResultFilterChipOption[]>(() => {
    const options: LabResultFilterChipOption[] = [
      {
        id: "all",
        label: t("all_lab_results.filter_all"),
        selected: !filtersActive,
        onPress: clearFilters,
      },
    ];

    for (const option of cycleFilterOptions) {
      if (option.id === "all") continue;
      options.push({
        ...option,
        selected: activeCycleFilter === option.id,
        onPress: () => onCycleFilterChange(option.id),
      });
    }

    for (const option of typeFilterOptions) {
      options.push({
        ...option,
        selected: activeTypeFilter === option.id,
        onPress: () => onTypeFilterChange(option.id),
      });
    }

    if (showStatusFilters) {
      for (const option of statusFilterOptions) {
        options.push({
          ...option,
          selected: activeStatusFilter === option.id,
          onPress: () => onStatusFilterChange(option.id),
        });
      }
    }

    return options;
  }, [
    activeCycleFilter,
    activeStatusFilter,
    activeTypeFilter,
    clearFilters,
    cycleFilterOptions,
    filtersActive,
    onCycleFilterChange,
    onStatusFilterChange,
    onTypeFilterChange,
    showStatusFilters,
    statusFilterOptions,
    t,
    typeFilterOptions,
  ]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View className="flex-row items-start justify-between">
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("all_lab_results.back")),
                "all_lab_results.back",
              );
              handleBack();
            }}
            className="mb-2 flex-row items-center gap-1"
          >
            <ChevronLeft size={18} color={EMBER.colors.warm} />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.warm }}
            >
              {t("all_lab_results.back")}
            </Text>
          </Pressable>
        </View>

        <View style={{ marginBottom: 24 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            }}
          >
            {cycleLabel
              ? t("all_lab_results.title_for_cycle", {
                  params: { cycle: cycleLabel },
                })
              : t("all_lab_results.title_all")}
          </Text>
          {labsDataState !== "unavailable" ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 19,
                color: EMBER.colors.textLight,
                marginTop: 8,
              }}
            >
              {countLabel}
            </Text>
          ) : null}
        </View>

        {/* Search bar + upload */}
        {labsDataState !== "unavailable" ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              marginBottom: showFilters ? 12 : 16,
            }}
          >
            <MoonSearchInput
              value={search}
              onChangeText={setSearch}
              placeholder={t("all_lab_results.search_placeholder")}
              onClear={() => setSearch("")}
              tone="glass"
              style={{ flex: 1, borderRadius: 12 }}
            />
            <Pressable
              onPress={() => {
                // Icon-only upload control with no visible copy to name it.
                capturePress("upload_lab_result");
                handleUpload();
              }}
              style={{
                ...(GLASS_CARD_STYLE as object),
                width: 50,
                height: 44,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <Animated.Image
                source={require("@src/apps/moon/assets/images/care-hero-glow-gradient.png")}
                style={{
                  position: "absolute",
                  left: -7,
                  top: -8,
                  width: 64,
                  height: 60,
                  opacity: 1,
                  transform: [
                    {
                      translateX: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-5, 6],
                      }),
                    },
                    {
                      translateY: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [3, -4],
                      }),
                    },
                    {
                      scale: uploadButtonDrift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1.02, 1.08],
                      }),
                    },
                  ],
                }}
                resizeMode="cover"
              />
              <View style={{ zIndex: 2, elevation: 2 }}>
                <Plus size={19} color="#fff" strokeWidth={2.25} />
              </View>
            </Pressable>
          </View>
        ) : null}

        {labsDataState !== "unavailable" && showFilters ? (
          <LabResultFilterRail options={visibleFilterOptions} />
        ) : null}

        {/* Results */}
        <View style={{ gap: 9 }}>
          {labsDataState === "unavailable" ? (
            <ClinicalDataStatus
              state="unavailable"
              title={t("clinical_data.unavailable_title")}
              message={t("clinical_data.unavailable_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={retryLabResults}
              isRetrying={testResultsQuery.isFetching}
              testID="lab-results-unavailable"
            />
          ) : (
            <>
              {labsDataState === "stale" ? (
                <ClinicalDataStatus
                  state="stale"
                  message={t("clinical_data.stale_body")}
                  retryLabel={t("clinical_data.retry")}
                  retryingLabel={t("clinical_data.retrying")}
                  onRetry={retryLabResults}
                  isRetrying={testResultsQuery.isFetching}
                  testID="lab-results-stale"
                />
              ) : null}
              {displayLabResultsLoading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <ListRowSkeleton
                      key={index}
                      showLeadingSquare
                      showSubtitle
                      showTrailing
                    />
                  ))
                : null}
              {!displayLabResultsLoading &&
                filtered.map((result) => {
                  const insight = labResultToInsight(result, t);
                  return (
                    <DocumentTestResultRow
                      key={result.id}
                      title={insight.testName}
                      subtitle={insight.testPurpose}
                      dateLabel={getResultDateLabel(result, t)}
                      value={insight.value}
                      unit={insight.unit}
                      category={insight.category}
                      badge={getResultRowBadge(result, t)}
                      pending={isResultPending(result)}
                      pendingOpacity={result.status === "scheduled" ? 0.2 : 1}
                      onPress={() => setSelectedResult(result)}
                    />
                  );
                })}

              {!displayLabResultsLoading &&
                filtered.length === 0 &&
                baseResults.length === 0 && (
                  <LabResultsEmptyState
                    onUpload={handleUpload}
                    onFilesSelected={handleDroppedUpload}
                    onManualEntry={() => openManualEntry()}
                  />
                )}

              {!displayLabResultsLoading &&
                filtered.length === 0 &&
                baseResults.length > 0 && (
                  <View
                    style={{
                      ...GLASS_CARD_STYLE,
                      paddingVertical: 32,
                      paddingHorizontal: 18,
                      alignItems: "center",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        color: EMBER.colors.textLight,
                        textAlign: "center",
                      }}
                    >
                      {t(noResultsKey)}
                    </Text>
                    {filtersActive ? (
                      <Pressable
                        onPress={() => {
                          capturePress(
                            pressLabel(t("all_lab_results.clear_filters")),
                            "all_lab_results.clear_filters",
                          );
                          clearFilters();
                        }}
                        hitSlop={8}
                        style={({ pressed }) => ({
                          marginTop: 12,
                          opacity: pressed ? 0.6 : 1,
                        })}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "700",
                            color: EMBER.colors.warm,
                          }}
                        >
                          {t("all_lab_results.clear_filters")}
                        </Text>
                      </Pressable>
                    ) : null}
                  </View>
                )}
            </>
          )}
        </View>
      </ScrollView>

      <TestResultDetailDialog
        result={selectedResult}
        point={
          selectedResult
            ? {
                id: selectedResult.id,
                date: selectedResult.date,
                value: selectedResult.value,
                clinicalAssessment: selectedResult.clinicalAssessment,
                clinicalAssessmentDetails:
                  selectedResult.clinicalAssessmentDetails,
                attachment: selectedResult.attachment,
                provenance: selectedResult.provenance,
              }
            : null
        }
        onClose={() => setSelectedResult(null)}
        onAttachSource={
          devPreviewLabResults
            ? undefined
            : async (id, attachmentId) => {
                await updateTestResult.mutateAsync({
                  testResultId: id,
                  data: { attachmentId },
                });
              }
        }
        cycleLink={selectedResult?.cycleLink}
        onOpenCycle={
          devPreviewLabResults
            ? undefined
            : (cycleLink) => {
                setSelectedResult(null);
                if (!cycleLink.cycleId) return;
                if (cycleLink.cycleId === currentCycleId) {
                  navigation.navigate("CycleDetail", {
                    cycleId: cycleLink.cycleId,
                  });
                } else {
                  navigation.navigate("PastCycleDetail", {
                    cycleId: cycleLink.cycleId,
                  });
                }
              }
        }
        onEdit={
          devPreviewLabResults
            ? undefined
            : async (id, data) => {
                await updateTestResult.mutateAsync({ testResultId: id, data });
                setSelectedResult(null);
              }
        }
        onDelete={
          devPreviewLabResults
            ? undefined
            : async (id) => {
                await deleteTestResult.mutateAsync(id);
                setSelectedResult(null);
              }
        }
        testId={selectedResult?.testId}
        onOpenTest={openTestFactsheet}
      />
      <DocumentUploadModal
        visible={uploadModalVisible}
        onClose={handleUploadClose}
        slot={null}
        defaultCategory="medical_records"
        defaultTags={["doctype:lab"]}
        initialFiles={pendingUploadFiles}
        autoPickOnOpen={!visualEvidenceRoute}
      />
      <ManualLabResultModal
        visible={manualEntryVisible}
        initialName={manualEntryName}
        initialType={manualEntryType}
        onOpenUpload={handleManualUpload}
        onClose={() => {
          setManualEntryVisible(false);
          setManualEntryName(null);
          setManualEntryType(null);
        }}
      />
    </GlassScreenLayout>
  );
}
